import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { Capture } from '../../interfaces/state/entities/capture.interface';
import { CaptureSessionsMockService } from '../capture-sessions/capture-sessions-mock.service';
import { ICapturesService } from '../../interfaces/services/captures-service.interface';
import {
  mockBytesFromMb,
  mockBytesString,
  mockHoursAgo,
  mockMinutesAgo,
  mockNormalizeFileSize,
} from '../mock/mock-data.utils';

@Injectable({ providedIn: 'root' })
export class CapturesMockService implements ICapturesService {
  private captureSessionsService = inject(CaptureSessionsMockService);
  private captures: Capture[] = [];
  private idCounter = 0;

  constructor() {
    const now = new Date();
    const mockCaptures: Capture[] = [
      {
        id: '1',
        sessionId: '1',
        name: 'Ground Floor - Entrance',
        fileName: 'entrance_001.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 16 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000),
        stationCount: 12,
        revisionNumber: 1,
        fileSize: '3.2 MB',
      },
      {
        id: '2',
        sessionId: '1',
        name: 'Ground Floor - Lobby',
        fileName: 'lobby_002.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000),
        stationCount: 18,
        revisionNumber: 2,
        fileSize: '4.1 MB',
      },
      {
        id: '3',
        sessionId: '1',
        name: 'Ground Floor - Corridor',
        fileName: 'corridor_video.mp4',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 13 * 24 * 60 * 60 * 1000),
        stationCount: 8,
        revisionNumber: 3,
        fileSize: '25.5 MB',
      },
      {
        id: '4',
        sessionId: '1',
        name: 'Ground Floor - Office Space',
        fileName: 'office_003.jpg',
        status: 'Active',
        dateCreated: new Date(now.getTime() - 12 * 24 * 60 * 60 * 1000),
        dateProcessed: undefined,
        stationCount: 0,
        revisionNumber: 4,
        fileSize: '2.8 MB',
      },
      {
        id: '5',
        sessionId: '1',
        name: 'Ground Floor - Storage',
        fileName: 'storage_video.insv',
        status: 'Failed',
        dateCreated: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000),
        dateProcessed: undefined,
        stationCount: 0,
        revisionNumber: 5,
        fileSize: '15.3 MB',
      },
      {
        id: '6',
        sessionId: '2',
        name: 'Ground Floor - Meeting Room',
        fileName: 'meeting_room.mp4',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 9 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
        stationCount: 10,
        revisionNumber: 1,
        fileSize: '32.1 MB',
      },
      {
        id: '7',
        sessionId: '2',
        name: 'Ground Floor - Kitchen',
        fileName: 'kitchen.mp4',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 8 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
        stationCount: 6,
        revisionNumber: 2,
        fileSize: '18.7 MB',
      },
      {
        id: '8',
        sessionId: '2',
        name: 'Ground Floor - Restroom',
        fileName: 'restroom.mp4',
        status: 'Active',
        dateCreated: new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000),
        dateProcessed: undefined,
        stationCount: 0,
        revisionNumber: 3,
        fileSize: '12.4 MB',
      },
      {
        id: '10',
        sessionId: '4',
        name: 'Basement - Storage Area 2',
        fileName: 'storage2.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 20 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 19 * 24 * 60 * 60 * 1000),
        stationCount: 22,
        revisionNumber: 2,
        fileSize: '4.2 MB',
      },
      {
        id: '9',
        sessionId: '4',
        name: 'Basement - Storage Area 1',
        fileName: 'storage1.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 21 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 19 * 24 * 60 * 60 * 1000),
        stationCount: 20,
        revisionNumber: 1,
        fileSize: '3.5 MB',
      },
      {
        id: '11',
        sessionId: '4',
        name: 'Basement - Utility Room',
        fileName: 'utility.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 19 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 18 * 24 * 60 * 60 * 1000),
        stationCount: 14,
        revisionNumber: 3,
        fileSize: '2.9 MB',
      },
      {
        id: '12',
        sessionId: '4',
        name: 'Basement - Mechanical Room',
        fileName: 'mechanical.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 18 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 17 * 24 * 60 * 60 * 1000),
        stationCount: 16,
        revisionNumber: 4,
        fileSize: '3.8 MB',
      },
      {
        id: '13',
        sessionId: '4',
        name: 'Basement - Electrical Room',
        fileName: 'electrical.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 17 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 16 * 24 * 60 * 60 * 1000),
        stationCount: 12,
        revisionNumber: 5,
        fileSize: '2.6 MB',
      },
      {
        id: '14',
        sessionId: '4',
        name: 'Basement - Parking Area',
        fileName: 'parking.mp4',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 16 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000),
        stationCount: 30,
        revisionNumber: 6,
        fileSize: '45.2 MB',
      },
      {
        id: '15',
        sessionId: '4',
        name: 'Basement - Loading Bay',
        fileName: 'loading_bay.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000),
        stationCount: 18,
        revisionNumber: 7,
        fileSize: '4.5 MB',
      },
      {
        id: '16',
        sessionId: '4',
        name: 'Basement - Archive Room',
        fileName: 'archive.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 13 * 24 * 60 * 60 * 1000),
        stationCount: 10,
        revisionNumber: 8,
        fileSize: '2.3 MB',
      },
      {
        id: '17',
        sessionId: '4',
        name: 'Basement - Server Room',
        fileName: 'server.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 13 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 12 * 24 * 60 * 60 * 1000),
        stationCount: 8,
        revisionNumber: 9,
        fileSize: '2.1 MB',
      },
      {
        id: '18',
        sessionId: '4',
        name: 'Basement - Workshop',
        fileName: 'workshop.mp4',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 12 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 11 * 24 * 60 * 60 * 1000),
        stationCount: 25,
        revisionNumber: 10,
        fileSize: '38.7 MB',
      },
      {
        id: '19',
        sessionId: '4',
        name: 'Basement - Storage Area 3',
        fileName: 'storage3.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000),
        stationCount: 0,
        revisionNumber: 11,
        fileSize: '3.1 MB',
      },
      {
        id: '20',
        sessionId: '4',
        name: 'Basement - Cold Storage',
        fileName: 'cold_storage.jpg',
        status: 'Failed',
        dateCreated: new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000),
        dateProcessed: undefined,
        stationCount: 0,
        revisionNumber: 12,
        fileSize: '2.7 MB',
      },
      {
        id: '21',
        sessionId: '4',
        name: 'Basement - Maintenance Room',
        fileName: 'maintenance.jpg',
        status: 'Active',
        dateCreated: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000),
        dateProcessed: undefined,
        stationCount: 11,
        revisionNumber: 13,
        fileSize: '3.3 MB',
      },
      {
        id: '22',
        sessionId: '5',
        name: 'Main Floor - Reception',
        fileName: 'reception.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 13 * 24 * 60 * 60 * 1000),
        stationCount: 16,
        revisionNumber: 1,
        fileSize: '3.7 MB',
      },
      {
        id: '23',
        sessionId: '5',
        name: 'Main Floor - Showroom',
        fileName: 'showroom.mp4',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 13 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 12 * 24 * 60 * 60 * 1000),
        stationCount: 24,
        revisionNumber: 2,
        fileSize: '42.3 MB',
      },
      {
        id: '24',
        sessionId: '5',
        name: 'Main Floor - Sales Office',
        fileName: 'sales_office.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 12 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 11 * 24 * 60 * 60 * 1000),
        stationCount: 13,
        revisionNumber: 3,
        fileSize: '3.4 MB',
      },
      {
        id: '25',
        sessionId: '5',
        name: 'Main Floor - Customer Service',
        fileName: 'customer_service.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 11 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000),
        stationCount: 9,
        revisionNumber: 4,
        fileSize: '2.5 MB',
      },
      {
        id: '26',
        sessionId: '5',
        name: 'Main Floor - Break Room',
        fileName: 'break_room.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 9 * 24 * 60 * 60 * 1000),
        stationCount: 7,
        revisionNumber: 5,
        fileSize: '2.2 MB',
      },
      {
        id: '27',
        sessionId: '5',
        name: 'Main Floor - Storage',
        fileName: 'main_storage.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000),
        stationCount: 0,
        revisionNumber: 6,
        fileSize: '3.0 MB',
      },
      {
        id: '28',
        sessionId: '5',
        name: 'Main Floor - Restroom',
        fileName: 'main_restroom.jpg',
        status: 'Active',
        dateCreated: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000),
        dateProcessed: undefined,
        stationCount: 5,
        revisionNumber: 7,
        fileSize: '1.9 MB',
      },
      {
        id: '29',
        sessionId: '5',
        name: 'Main Floor - Back Office',
        fileName: 'back_office.mp4',
        status: 'Failed',
        dateCreated: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000),
        dateProcessed: undefined,
        stationCount: 0,
        revisionNumber: 8,
        fileSize: '28.6 MB',
      },
      {
        id: '30',
        sessionId: '6',
        name: 'Main Floor - Conference Room',
        fileName: 'conference.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000),
        stationCount: 14,
        revisionNumber: 1,
        fileSize: '4.8 MB',
      },
      {
        id: '31',
        sessionId: '6',
        name: 'Main Floor - Executive Office',
        fileName: 'executive.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000),
        stationCount: 11,
        revisionNumber: 2,
        fileSize: '3.6 MB',
      },
      {
        id: '32',
        sessionId: '7',
        name: 'Storage Area - Section A',
        fileName: 'section_a.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 9 * 24 * 60 * 60 * 1000),
        stationCount: 21,
        revisionNumber: 1,
        fileSize: '4.6 MB',
      },
      {
        id: '33',
        sessionId: '7',
        name: 'Storage Area - Section B',
        fileName: 'section_b.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 9 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 8 * 24 * 60 * 60 * 1000),
        stationCount: 19,
        revisionNumber: 2,
        fileSize: '4.3 MB',
      },
      {
        id: '34',
        sessionId: '7',
        name: 'Storage Area - Section C',
        fileName: 'section_c.mp4',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 8 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
        stationCount: 23,
        revisionNumber: 3,
        fileSize: '35.9 MB',
      },
      {
        id: '35',
        sessionId: '7',
        name: 'Storage Area - Section D',
        fileName: 'section_d.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000),
        stationCount: 20,
        revisionNumber: 4,
        fileSize: '4.0 MB',
      },
      {
        id: '36',
        sessionId: '7',
        name: 'Storage Area - Section E',
        fileName: 'section_e.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000),
        stationCount: 0,
        revisionNumber: 5,
        fileSize: '3.9 MB',
      },
      {
        id: '37',
        sessionId: '7',
        name: 'Storage Area - Section F',
        fileName: 'section_f.jpg',
        status: 'Active',
        dateCreated: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000),
        dateProcessed: undefined,
        stationCount: 16,
        revisionNumber: 6,
        fileSize: '3.7 MB',
      },
      {
        id: '38',
        sessionId: '8',
        name: 'Loading Dock - Bay 1',
        fileName: 'bay1.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000),
        stationCount: 12,
        revisionNumber: 1,
        fileSize: '3.2 MB',
      },
      {
        id: '39',
        sessionId: '8',
        name: 'Loading Dock - Bay 2',
        fileName: 'bay2.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000),
        stationCount: 10,
        revisionNumber: 2,
        fileSize: '2.9 MB',
      },
      {
        id: '40',
        sessionId: '8',
        name: 'Loading Dock - Bay 3',
        fileName: 'bay3.mp4',
        status: 'Active',
        dateCreated: new Date(now.getTime() - 0.5 * 24 * 60 * 60 * 1000),
        dateProcessed: undefined,
        stationCount: 0,
        revisionNumber: 4,
        fileSize: '22.4 MB',
      },
      {
        id: '41',
        sessionId: '8',
        name: 'Loading Dock - Bay 4',
        fileName: 'bay4.jpg',
        status: 'Processed',
        dateCreated: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000),
        dateProcessed: new Date(now.getTime() - 0.5 * 24 * 60 * 60 * 1000),
        stationCount: 9,
        revisionNumber: 3,
        fileSize: '2.7 MB',
      },
      {
        id: '42',
        sessionId: '1',
        name: 'Ground Floor - Upload Failed Demo',
        fileName: 'upload_failed_demo.jpg',
        status: 'UploadFailed',
        dateCreated: mockMinutesAgo(now, 40),
        dateProcessed: undefined,
        stationCount: 0,
        revisionNumber: 6,
        fileSize: mockBytesFromMb(2.4),
      },
      {
        id: '43',
        sessionId: '1',
        name: 'Ground Floor - Uploaded Demo',
        fileName: 'uploaded_demo.jpg',
        status: 'Uploaded',
        dateCreated: mockHoursAgo(now, 4),
        dateProcessed: undefined,
        stationCount: 0,
        revisionNumber: 7,
        fileSize: mockBytesFromMb(2.1),
      },
      {
        id: '44',
        sessionId: '1',
        name: 'Ground Floor - Pending Expired Demo',
        fileName: 'pending_expired_demo.jpg',
        status: 'Pending',
        dateCreated: mockHoursAgo(now, 2),
        dateProcessed: undefined,
        stationCount: 0,
        revisionNumber: 8,
        fileSize: mockBytesFromMb(1.8),
      },
      {
        id: '45',
        sessionId: '2',
        name: 'Ground Floor - Processing Retry Demo',
        fileName: 'processing_retry_demo.mp4',
        status: 'Uploaded',
        dateCreated: mockMinutesAgo(now, 25),
        dateProcessed: undefined,
        stationCount: 0,
        revisionNumber: 4,
        fileSize: mockBytesFromMb(18.5),
      },
    ];

    this.captures = mockCaptures.map((capture) => ({
      ...capture,
      captureAt: capture.dateCreated,
      fileSize: mockNormalizeFileSize(capture.fileSize),
      processId:
        capture.processId ??
        (capture.status === 'Processed' ? `cap-proc-${capture.id}` : undefined),
    }));
    this.idCounter = mockCaptures.length;
  }

  getCaptures(sessionId: string): Observable<Capture[]> {
    return of(this.captures.filter((c) => c.sessionId === sessionId)).pipe(delay(100));
  }

  addCapture(
    sessionId: string,
    name: string,
    file: File,
    _position: { x: string; y: string }
  ): Observable<Capture> {
    const fileName = file.name;
    const fileSize = file.size;
    const revisionNumber =
      this.captures.filter((c) => c.sessionId === sessionId).length + 1;
    const now = new Date();
    const capture: Capture = {
      id: String(++this.idCounter),
      sessionId,
      name,
      fileName,
      status: 'Pending',
      dateCreated: now,
      captureAt: new Date(file.lastModified),
      dateProcessed: undefined,
      stationCount: 0,
      revisionNumber,
      fileSize: fileSize ? mockBytesString(fileSize) : undefined,
    };
    this.captures.push(capture);
    this.captureSessionsService.incrementCaptureCount(sessionId);
    return of(capture).pipe(delay(100));
  }

  retryProcessingCapture(captureId: string, fileSize: number): Observable<void> {
    const capture = this.captures.find((c) => c.id === captureId);
    if (capture) {
      const now = new Date();
      capture.status = 'Processed';
      capture.processId = capture.processId ?? `cap-proc-${capture.id}`;
      if (fileSize > 0) {
        capture.fileSize = mockBytesString(fileSize);
      }
      capture.dateProcessed = now;
    }
    return of(undefined).pipe(delay(100));
  }
}
